import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { connect, useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';

import SVG from 'components/base/SVG';
import { addListener, removeListener, isOnElement } from 'utils/DOM/onOuterClick';
import AddToInvestorListModal from 'app/components/AddToInvestorListModal';
import { resolveInvestorContacts } from 'data/investors/investorProfileService';
import InfoAlert from 'app/components/InfoAlert';
import CampaignActivitySettingsModal, {
  CAMPAIGN_ACTIVITY_SETTINGS_CONTEXT,
  CAMPAIGN_ACTIVITY_TYPE,
} from 'app/Campaigns/CampaignActivitySettingsModal';
import { createDialerCampaign, openLeadAutomatorUpgrade } from 'data/user/actions';
import { selectProfile, selectListManagementEnabled } from 'data/user';
import { isLegacyAccountPlan } from 'data/user/selectors';
import FullAccessRequiredModal from 'components/ClickToDial/FullAccessRequiredModal/FullAccessRequiredModal';
import LeadAutomatorUpgrade from 'components/DialerCampaign/LeadAutomatorUpgrade/LeadAutomatorUpgrade';
import LeaveMessagePopup from 'app/AccountNew/modals/LeaveMessage';
import { checkDialerCampaignAccess } from 'app/Campaigns/dialerCampaignAccess';
import { loadCampaign, loadCampaigns } from 'data/campaigns/actions';
import {
  SortOptions,
  setInvestorFilter,
  searchInvestors,
  selectInvestorResults,
  selectInvestorTotal,
  selectInvestorFilters,
  selectSubject,
  selectSubjectComplete,
  selectLoading,
  selectError,
  selectInvestorContactOverrides,
  setInvestorContacts,
} from 'data/investors';

import Dropdown from '../SearchFilter/Dropdown';
import SelectCheckbox from './SelectCheckbox';
import SelectionContext from './SelectionContext';
import ActionsMenu from './ActionsMenu';
import InvestorCard from './InvestorCard';
import useSkipTraceInvestors from '../useSkipTraceInvestors';
import css from './style.scss';

// Post-search results pane — Figma node 3:15998, right column (6:520): a
// selection/count/sort header over the investor cards.
//
// Order is whatever the API returned; the sort control changes the request, so
// re-sorting is a round trip rather than a client-side shuffle.
const Results = ({
  investors: resultInvestors,
  total,
  filters,
  error,
  setInvestorFilter,
  searchInvestors,
  subject,
  subjectComplete,
  loading,
  history,
}) => {
  const dispatch = useDispatch();
  const profile = useSelector(selectProfile);
  const lmEnabled = useSelector(selectListManagementEnabled);
  const isLegacyAccount = useSelector(isLegacyAccountPlan);
  const [selected, setSelected] = useState([]);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [campaignActivity, setCampaignActivity] = useState(null);
  const actionsRef = useRef(null);
  const { skipTrace } = useSkipTraceInvestors();

  // Stable handler identities for the memoized InvestorCards: with a fresh
  // function per render every card re-rendered on every checkbox click (105
  // heavy cards for one toggle). `refreshRef` is pointed at the real
  // refreshResults below, which closes over per-render search state.
  const toggleOne = useCallback(
    (id) =>
      setSelected((prev) =>
        prev.indexOf(id) === -1 ? prev.concat(id) : prev.filter((s) => s !== id)
      ),
    []
  );
  const selectedIds = useMemo(() => new Set(selected), [selected]);
  const refreshRef = useRef(null);
  const refreshFromCard = useCallback(() => refreshRef.current?.(), []);

  // Contacts edited from an investor's Profile (ATAI-467). The search results
  // are a one-time snapshot in the store, so a phone number added in the
  // Profile never reached the card until the next search. The Profile reports
  // its re-read contacts here and they're layered over the matching result;
  // cleared on every new search below, since that response is fresh anyway.
  // Only the edited investor gets a new object, so the other memoized cards
  // don't re-render.
  //
  // ATAI-554: held in the investors slice rather than here, so an edit made
  // from the map card's Details (which never passed this callback) reaches
  // these cards too; the slice clears it on every new search.
  const contactOverrides = useSelector(selectInvestorContactOverrides);
  const handleContactsUpdated = useCallback(
    (investorId, contacts) => dispatch(setInvestorContacts(investorId, contacts)),
    [dispatch]
  );
  const investors = useMemo(
    () =>
      (resultInvestors || []).map((investor) =>
        Object.prototype.hasOwnProperty.call(contactOverrides, investor.id)
          ? { ...investor, contacts: contactOverrides[investor.id] }
          : investor
      ),
    [resultInvestors, contactOverrides]
  );

  // `selected` is local state, so it outlives the result set it was picked
  // from: after a new search or a filter change the header kept counting ids
  // that are no longer on screen (ATAI-466). The reducer swaps in a new
  // `investors` array on every completed search (new property, changed
  // filter or sort, post-skip-trace refresh), so clear the selection then.
  // Pruning to ids still present isn't enough: investors recur across
  // nearby searches, which left stale ids selected.
  useEffect(() => {
    setSelected((prev) => (prev.length ? [] : prev));
  }, [resultInvestors]);

  // Clearing only on completion left the old count in the header for the
  // whole request — about a second after picking a new property, while the
  // previous results stay on screen (ATAI-466 reopen). Clear as soon as a
  // search starts too. Only the investor search sets the slice's `loading`,
  // so saving or skip tracing the selection never trips this.
  useEffect(() => {
    if (loading) setSelected((prev) => (prev.length ? [] : prev));
  }, [loading]);

  useEffect(() => {
    if (!actionsOpen) return undefined;

    const handleOuterClick = (ev) => {
      if (!isOnElement(ev, actionsRef.current)) setActionsOpen(false);
    };

    addListener(handleOuterClick);
    return () => removeListener(handleOuterClick);
  }, [actionsOpen]);

  if (error) {
    return (
      <div className={css.notice}>
        <p className={css.notice__title}>We couldn’t run that search</p>
        <p className={css.notice__body}>{String(error)}</p>
      </div>
    );
  }

  if (!investors || !investors.length) {
    return (
      <div className={css.notice}>
        <p className={css.notice__title}>No investors found</p>
        <p className={css.notice__body}>Try widening the search radius or removing some filters.</p>
      </div>
    );
  }

  const sort = filters.get('sort');
  const allSelected = selected.length === investors.length;
  // Mixed selection (ATAI-512) — some but not all results are selected.
  const partiallySelected = selected.length > 0 && !allSelected;

  // Sorting is applied server-side (investor-service's InvestorSorter orders the
  // result set by the requested mode), so changing it is a round trip: store the
  // choice and re-run the search immediately rather than waiting for another
  // Find click. The request is built from the updated filters directly, since
  // the prop from the store has not re-rendered yet at this point.
  const handleSortChange = (value) => {
    if (value === sort) return;

    setInvestorFilter('sort', value);
  };

  // Phase 1 loads the whole capped set, so "select all" genuinely covers every
  // result — there is no further page to pull.
  // (ATAI-512) Click clears any selection, full or partial, matching Search's checkbox.
  const toggleAll = () => setSelected(selected.length ? [] : investors.map((i) => i.id));

  const selectedInvestors = () =>
    investors.filter((investor) => selected.indexOf(investor.id) !== -1);

  // Add to Investor List (ATAI-180 / S10, PR #1662 on propstream-be). Its own
  // dedicated modal (not the generic AddToMarketingListModal) — it owns the
  // whole save flow itself; this just hands it the selected investor objects.
  const handleAddToInvestorList = () => {
    setActionsOpen(false);

    dispatch(AddToInvestorListModal.open({ investors: selectedInvestors() }));
  };

  // The result set never self-updates (S4 build-spec), so a card that just
  // got Skip Traced needs a deliberate refresh to show its contacts. Getting
  // there via each investor's own *profile* looked tempting but is wrong:
  // that endpoint 404s unless something already persisted a snapshot for it
  // (e.g. "Add to Investor List") — a plain, never-opened search result has
  // none. The search endpoint has no such dependency (stateless, live, and
  // already gates every result's contacts against the DB per ATAI-226), so
  // re-running it is the one refresh that works regardless of how many
  // investors were traced or whether they'd ever been touched before.
  const refreshResults = () => {
    if (!subjectComplete || loading) return;
    searchInvestors(subject, filters);
  };
  refreshRef.current = refreshResults;

  // Skip Trace (ATAI-115 / S9). Materializes the selected investors then
  // Skip Trace and both campaign channels act on contacts, not investors, so
  // each one routes through the PRD's "Mandatory List-Saving Policy": the
  // selection has to exist as an Investor List before there are contacts to
  // act on. What follows is the two ways that condition gets met.
  const isSaved = (investor) =>
    Array.isArray(investor?.savedGroups) && investor.savedGroups.length > 0;

  // An investor materializes to exactly ONE contact row, shared across every
  // list it is saved to (S7 dedupes on the BatchData investor id), so the first
  // entry carrying a contactId is the answer — the rest repeat it. An investor
  // with no contact yet is simply absent from the map.
  const pickContactId = (groups) =>
    groups?.find((group) => group.contactId != null)?.contactId ?? null;

  const handOffContactIds = (contactIds, next, justSaved) => {
    // Without this the action would just never happen and look like the menu
    // item did nothing — and in the save path the save has already succeeded
    // and toasted by now, so silence would be actively misleading.
    if (!contactIds.length) {
      dispatch(
        InfoAlert.open({
          title: 'Could not continue',
          infoText: justSaved
            ? 'The investors were saved, but we could not load their contacts. Please try the action again from the Investor List.'
            : 'We could not load the contacts for the selected investors. Please try the action again from the Investor List.',
        })
      );
      return;
    }

    next(contactIds);
  };

  const resolveThenContinue = (chosen, next, justSaved) =>
    resolveInvestorContacts(chosen.map((investor) => investor.id))
      .then((groupsByInvestor) => {
        handOffContactIds(
          chosen
            .map((investor) => pickContactId(groupsByInvestor?.data?.[investor.id]))
            .filter((id) => id != null),
          next,
          justSaved
        );
      })
      .catch((error) => {
        console.error('Could not resolve the selected investors to contacts:', error);
        dispatch(
          InfoAlert.open({
            title: 'Could not continue',
            infoText:
              'We could not load the contacts for the selected investors. Please try again.',
          })
        );
      });

  const withContactIds = (next) => {
    const chosen = selectedInvestors();

    // Already-saved selection → skip the modal. The Add to Investor List step
    // is only there to CREATE the contacts these actions need; when every
    // selected investor already carries `savedGroups` those contacts exist, so
    // asking the user to pick a list again is a dialog that changes nothing.
    //
    // ALL of them, not some: a partial selection still has investors with no
    // contact to act on, and the modal saves the whole selection in one go —
    // which is also why the unsaved case keeps passing `chosen`, not just the
    // unsaved subset. Re-saving an already-saved investor is idempotent
    // (propstream-be dedupes on the BatchData investor id).
    //
    // `savedGroups` comes from the S2 search response, which is fetched once
    // and never self-updates — an investor saved from its own card bookmark
    // during this session still reads as unsaved here. That errs toward showing
    // the modal, so a stale read costs an extra dialog, never correctness.
    if (chosen.length && chosen.every(isSaved)) {
      const fromCards = chosen.map((investor) => pickContactId(investor.savedGroups));

      // The search route does not populate contactId on savedGroups in every
      // environment (it carries groupId/groupName only — see Profile/index.js,
      // which hits the saved-groups endpoint for exactly this reason). Acting
      // on a short list would silently drop investors from the campaign, so
      // anything missing sends the whole selection through the batched resolve.
      if (fromCards.every((id) => id != null)) {
        handOffContactIds(fromCards, next, false);
      } else {
        resolveThenContinue(chosen, next, false);
      }

      return;
    }

    // The resolve has to happen AFTER the save, not before: the contact rows
    // the ids come from are what the save creates. The store's `savedGroups`
    // is stale for anything just saved, so this path never uses the fast path
    // above — it always asks the service.
    dispatch(
      AddToInvestorListModal.open({
        investors: chosen,
        onSaved: () => resolveThenContinue(chosen, next, true),
      })
    );
  };

  //   // Same two-step the existing SkipTraceHandler does: estimate first so an
  //   // append job that is already running surfaces as an alert rather than a
  //   // second order, then open the real modal with the contact ids.
  //   const handleSkipTrace = () => {
  //     setActionsOpen(false);

  //     withContactIds((contactIds) => {
  //       dispatch(
  //         getAppendJobEstimate({ contactIds, appendMode: 'BOTH', reprocessEnabled: false }, ({ response, error }) => {
  //           if (error) {
  //             console.error('Could not estimate the skip trace:', error);
  //             return;
  //           }

  //           if (response?.message?.includes('There is an existing append job')) {
  //             dispatch(
  //               InfoAlert.open({
  //                 title: 'Skip Trace in Progress',
  //                 infoText:
  //                   'There is a Skip Trace currently in progress. Please try again once it is complete.',
  //               })
  //             );
  //             return;
  //           }

  //           dispatch(SkipTraceModal.open({ reprocessEnabled: false, contactIds }));
  //         })
  //       );
  //     });
  //   };

  // Skip Trace (ATAI-115 / S9). Materializes the selected investors then
  // opens the existing Skip Trace modal with their contact ids — see
  // ../useSkipTraceInvestors, shared with the per-card "Skip Trace" button
  // (which passes the same refreshResults as its own onSkipTraced).
  const handleSkipTrace = () => {
    setActionsOpen(false);
    const selectedInvestors = investors.filter((investor) => selected.indexOf(investor.id) !== -1);

    skipTrace(selectedInvestors, { onSuccess: refreshResults });
  };

  // New Campaign Activity — the entry point only. Per the PRD there is "no new
  // campaign builder": this loads the marketing campaigns, opens the existing
  // Campaign Activity settings dialog, and hands the contact ids straight into
  // the existing Email / Dialer modules, exactly as Contacts/ContactsList does
  // for a contact selection.
  const openCampaignActivity = (activityType) => {
    setActionsOpen(false);

    // The menu items are already disabled without a selection, so this is a
    // backstop rather than the primary guard — but it keeps the wording
    // identical to My Property and the Investor List if it is ever reachable.
    if (!selected.length) {
      dispatch(
        InfoAlert.open({
          title: 'No Records Selected',
          infoText: `Please select one or more records before initiating a ${
            activityType === CAMPAIGN_ACTIVITY_TYPE.DIALER ? 'Dialer' : 'Email'
          } Campaign.`,
        })
      );
      return;
    }

    // Same entitlement gate My Property and the Investor List apply. It has to
    // run BEFORE withContactIds: that helper opens the Add to Investor List
    // modal and saves the selection, so gating afterwards would leave a user
    // who is not entitled to the dialer with investors already written to a
    // list and a blocked action — and would let the Outreach search be used to
    // route around a restriction that applies everywhere else.
    if (
      activityType === CAMPAIGN_ACTIVITY_TYPE.DIALER &&
      !checkDialerCampaignAccess({
        profile,
        lmEnabled,
        isLegacyAccount,
        openFullAccessRequiredModal: (props) => dispatch(FullAccessRequiredModal.open(props)),
        closeFullAccessRequiredModal: () => dispatch(FullAccessRequiredModal.close()),
        openLeadAutomatorUpgrade: (isOpen) => dispatch(openLeadAutomatorUpgrade(isOpen)),
        leaveMessagePopup: (props) => dispatch(LeaveMessagePopup.open(props)),
        history,
      })
    ) {
      return;
    }

    withContactIds((contactIds) => {
      Promise.resolve(dispatch(loadCampaigns()))
        .then(() => setCampaignActivity({ activityType, contactIds }))
        .catch(() =>
          dispatch(
            InfoAlert.open({
              title: 'Error',
              infoText: 'Could not load campaigns. Please try again.',
            })
          )
        );
    });
  };

  const closeCampaignActivity = () => setCampaignActivity(null);

  const handleCampaignSave = (name, marketingCampaignId) => {
    const { activityType, contactIds } = campaignActivity;

    if (activityType === CAMPAIGN_ACTIVITY_TYPE.EMAIL) {
      closeCampaignActivity();
      history.push({
        pathname: `/campaign/${marketingCampaignId}/email/settings`,
        state: { contactIds, name, marketingCampaignId },
      });
      return Promise.resolve();
    }

    // Known limitation, confirmed in code and flagged on the S11 design: the
    // dialer only lists a contact as a lead when it has a saved_property_id
    // (DialerService.getCampaignLeads filters on it, and Dashboard's
    // DialerEligibility refuses to create at all without one). Investors
    // materialized by S7 have none, so the activity is created here but its
    // call list comes back empty until that rule is settled. Email is
    // unaffected.
    return dispatch(
      createDialerCampaign({
        name,
        marketingCampaignId,
        contactIds,
        originatingListName: 'Investor Outreach',
      })
    )
      .then((res) => {
        const dialerId = res && res.id;
        if (!dialerId) throw new Error('Invalid dialer response');

        closeCampaignActivity();
        history.push(`/campaign/${marketingCampaignId}/dialer/${dialerId}`);
        dispatch(loadCampaign(marketingCampaignId));
      })
      .catch(() => {
        closeCampaignActivity();
        dispatch(
          InfoAlert.open({
            title: 'Error',
            infoText: 'Could not create the dialer activity. Please try again.',
          })
        );
      });
  };

  console.log('investors', campaignActivity);

  return (
    <div className={css.container}>
      <div className={css.header}>
        <div className={css.header__left}>
          <SelectCheckbox
            id="investor-select-all"
            checked={allSelected}
            indeterminate={partiallySelected}
            onChange={toggleAll}
            label="Select all investors"
          />
          {selected.length ? (
            <div className={css.countGroup}>
              <p className={css.count}>
                <strong>{selected.length.toLocaleString('en-US')}</strong>
                <span>SELECTED</span>
              </p>
              <p className={css.countTotal}>
                <strong>{Number(total || investors.length).toLocaleString('en-US')}</strong>
                <span>INVESTORS</span>
              </p>
            </div>
          ) : (
            <p className={css.count}>
              <strong>{Number(total || investors.length).toLocaleString('en-US')}</strong>
              <span>INVESTORS</span>
            </p>
          )}
        </div>
        <div className={css.header__right}>
          <Dropdown
            value={sort}
            options={SortOptions}
            onChange={handleSortChange}
            width={211}
            compact
            menuClassName={css.sortMenu}
            itemSelectedClassName={css.sortItemSelected}
          />
          {/* S10's "Add to Investor List" and S9's "Skip Trace" are wired;
              Campaign / Export (S11/S12) are separate stories, not yet added
              here. */}
          <div className={css.actionsMenu} ref={actionsRef}>
            {/* Open, the pill darkens to Teal/1400 and the caret flips up
                (Figma 9:32499 vs the closed 9:32511) — the menu being on
                screen is not by itself the control's open state. */}
            <button
              type="button"
              className={classNames(css.actions, { [css.actionsOpen]: actionsOpen })}
              onClick={() => setActionsOpen(!actionsOpen)}
            >
              Actions
              <SVG
                icon="iconInvestorActionsCaret"
                className={classNames(css.actions__caret, {
                  [css.actions__caretUp]: actionsOpen,
                })}
              />
            </button>
            {/* The design opens this menu from the unselected header state
                ("260 INVESTORS", select-all unchecked), so the button itself is
                never disabled; it is the individual actions that need a
                selection to act on. */}
            {actionsOpen && (
              <ActionsMenu
                disabled={!selected.length}
                onSkipTrace={handleSkipTrace}
                onAddToInvestorList={handleAddToInvestorList}
                onDialer={() => openCampaignActivity(CAMPAIGN_ACTIVITY_TYPE.DIALER)}
                onEmail={() => openCampaignActivity(CAMPAIGN_ACTIVITY_TYPE.EMAIL)}
              />
            )}
          </div>
        </div>
      </div>

      <CampaignActivitySettingsModal
        isOpen={Boolean(campaignActivity)}
        onClose={closeCampaignActivity}
        activityType={campaignActivity?.activityType}
        context={CAMPAIGN_ACTIVITY_SETTINGS_CONTEXT.MY_PROPERTY}
        selectionContactIds={campaignActivity?.contactIds || []}
        onSave={handleCampaignSave}
      />

      {/* openLeadAutomatorUpgrade only sets a redux flag — this is what renders
          the upsell the gate above triggers. */}
      <LeadAutomatorUpgrade />

      <SelectionContext.Provider value={selectedIds}>
        <ul className={css.list}>
          {investors.map((investor, index) => (
            <InvestorCard
              key={investor.id}
              investor={investor}
              rank={index + 1}
              // Design shows the label on the top card only (#1 BEST MATCH),
              // plain #N below it.
              rankLabel={index === 0 && sort === 'BEST_MATCH' ? 'BEST MATCH' : ''}
              onToggle={toggleOne}
              onSkipTraced={refreshFromCard}
              onContactsUpdated={handleContactsUpdated}
            />
          ))}
        </ul>
      </SelectionContext.Provider>
    </div>
  );
};

// withRouter rather than useHistory: this app pairs react-router-dom 5.3.4 with
// React 18, where v5's context hooks read back null (the same reason the one
// other useHistory import in the codebase, in Contacts/ContactEditor, is
// commented out). Every other routed component here takes `history` as a prop.
export default withRouter(
  connect(
    (state) => ({
      investors: selectInvestorResults(state),
      total: selectInvestorTotal(state),
      filters: selectInvestorFilters(state),
      subject: selectSubject(state),
      subjectComplete: selectSubjectComplete(state),
      loading: selectLoading(state),
      error: selectError(state),
    }),
    { setInvestorFilter, searchInvestors }
  )(Results)
);
